import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../../auth'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'

type Account = { id: string; fullName: string; email: string; isAdmin: boolean; isActive: boolean }
async function api(path: string, body?: unknown) {
  const response = await fetch(`/api/auth/${path}`, body === undefined ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  })
  const result = await response.json()
  if (!response.ok) throw new Error(result.error ?? 'Could not save account')
  return result
}
function AccountForm({ account, self, onSaved }: { account: Account; self?: boolean; onSaved: (signIn: boolean) => void }) {
  const [name, setName] = useState(account.fullName)
  const [email, setEmail] = useState(account.email)
  const [password, setPassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [admin, setAdmin] = useState(account.isAdmin)
  const [active, setActive] = useState(account.isActive)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('')
    try {
      const result = await api(self ? 'profile' : account.id ? `users/${encodeURIComponent(account.id)}` : 'users', {
        fullName: name, email, password, currentPassword, isAdmin: admin, isActive: active,
      })
      setPassword(''); setCurrentPassword(''); setMessage('Saved.'); onSaved(result.signInRequired)
    } catch (error) { setMessage((error as Error).message) }
    finally { setBusy(false) }
  }
  return <form onSubmit={submit} className="space-y-4 rounded-lg border p-4 max-w-xl">
    <h3 className="font-semibold">{self ? 'My profile & login' : account.id ? 'Edit user' : 'Add user'}</h3>
    <label className="block space-y-1"><span>Full name</span><Input required maxLength={200} autoComplete="name" value={name} onChange={e => setName(e.target.value)} /></label>
    <label className="block space-y-1"><span>Login email</span><Input required type="email" maxLength={254} autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} /></label>
    <label className="block space-y-1"><span>{account.id ? 'New password (leave blank to keep current)' : 'Initial password'}</span><Input type="password" autoComplete="new-password" required={!account.id} minLength={12} maxLength={256} value={password} onChange={e => setPassword(e.target.value)} /></label>
    {self && <label className="block space-y-1"><span>Current password (required to change email or password)</span><Input type="password" autoComplete="current-password" maxLength={256} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} /></label>}
    {!self && <div className="flex gap-4"><label><input type="checkbox" checked={admin} onChange={e => setAdmin(e.target.checked)} /> Administrator</label><label><input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active</label></div>}
    <p className="text-sm text-muted-foreground">Passwords must have at least 12 characters. Login changes sign out existing sessions.</p>
    <Button disabled={busy} type="submit">{busy ? 'Saving…' : 'Save'}</Button>
    {message && <p role="status">{message}</p>}
  </form>
}
export default function AccountSettings() {
  const { user } = useAuth()
  const [users, setUsers] = useState<Account[]>([])
  const [admin, setAdmin] = useState(false)
  const [selected, setSelected] = useState<Account | null>(null)
  const [error, setError] = useState('')
  async function load() {
    try {
      const permissions = await api('permissions'); setAdmin(permissions.isAdmin)
      if (permissions.isAdmin) setUsers((await api('users')).users)
    } catch (e) { setError((e as Error).message) }
  }
  useEffect(() => { void load() }, [])
  if (!user) return null
  return <div className="space-y-6">
    <AccountForm self account={{ ...user, isAdmin: admin, isActive: true }} onSaved={signIn => window.location.assign(signIn ? '/login' : window.location.href)} />
    {error && <p role="alert">{error}</p>}
    {admin && <section className="space-y-4"><h2 className="text-lg font-semibold">Manage users</h2>
      <Button onClick={() => setSelected({ id: '', fullName: '', email: '', isAdmin: false, isActive: true })}>Add user</Button>
      <ul className="space-y-2">{users.map(account => <li key={account.id} className="flex flex-wrap items-center gap-3 border rounded p-3">
        <span className="flex-1 break-all">{account.fullName} · {account.email} · {account.isAdmin ? 'Administrator' : 'User'} · {account.isActive ? 'Active' : 'Inactive'}</span>
        <Button variant="outline" onClick={() => setSelected(account)}>Edit</Button>
      </li>)}</ul>
      {selected && <AccountForm key={selected.id} account={selected} onSaved={signIn => { if (signIn) window.location.assign('/login'); else { setSelected(null); void load() } }} />}
    </section>}
  </div>
}
